// queryThunks.js - Updated with missing thunks

import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';

/**
 * GET /api/queries/?limit=<limit>&offset=<offset>
 */
export const fetchClientQueriesThunk = createAsyncThunk(
  'queries/fetchAll',
  async ({ offset = 0, limit = 20, ...filters } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ limit, offset });
      
      // Add additional filters if provided
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });
      
      const response = await axiosInstance.get(`/api/queries/?${params.toString()}`);
      return response.data; // paginated
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 
        error.message || 
        'Failed to fetch client queries'
      );
    }
  }
);

/**
 * GET /api/queries/{id}/
 */
export const fetchClientQueryByIdThunk = createAsyncThunk(
  'queries/fetchById',
  async (queryId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/queries/${queryId}/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 
        error.message || 
        'Failed to fetch client query'
      );
    }
  }
);

/**
 * POST /api/queries/
 */
export const createClientQueryThunk = createAsyncThunk(
  'queries/create',
  async (queryData, { rejectWithValue }) => {
    try {
      // Handle both FormData and regular objects
      const config = {};
      if (queryData instanceof FormData) {
        config.headers = {
          'Content-Type': 'multipart/form-data',
        };
      }
      
      const response = await axiosInstance.post('/api/queries/', queryData, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 
        error.response?.data?.non_field_errors?.[0] ||
        error.message || 
        'Failed to create client query'
      );
    }
  }
);

/**
 * PATCH /api/queries/{id}/
 */
export const updateClientQueryThunk = createAsyncThunk(
  'queries/update',
  async ({ queryId, updates }, { rejectWithValue }) => {
    try {
      // Handle both FormData and regular objects
      const config = {};
      if (updates instanceof FormData) {
        config.headers = {
          'Content-Type': 'multipart/form-data',
        };
      }
      
      const response = await axiosInstance.patch(`/api/queries/${queryId}/`, updates, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 
        error.response?.data?.non_field_errors?.[0] ||
        error.message || 
        'Failed to update client query'
      );
    }
  }
);

/**
 * DELETE /api/queries/{id}/
 */
export const deleteClientQueryThunk = createAsyncThunk(
  'queries/delete',
  async (queryId, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/api/queries/${queryId}/`);
      return queryId; // Return the ID for removal from state
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 
        error.message || 
        'Failed to delete client query'
      );
    }
  }
);

/**
 * POST /api/queries/{id}/assign/
 * Assign query to an employee
 */
export const assignClientQueryThunk = createAsyncThunk(
  'queries/assign',
  async ({ queryId, employeeId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/queries/${queryId}/assign/`, {
        employee: employeeId
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 
        error.message || 
        'Failed to assign client query'
      );
    }
  }
);

/**
 * PATCH /api/queries/{id}/status/
 * Update query status
 */
export const updateClientQueryStatusThunk = createAsyncThunk(
  'queries/updateStatus',
  async ({ queryId, status }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`/api/queries/${queryId}/status/`, {
        status
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 
        error.message || 
        'Failed to update query status'
      );
    }
  }
);

/**
 * GET /api/queries/summary/
 * Get queries summary/statistics
 */
export const fetchClientQueriesSummaryThunk = createAsyncThunk(
  'queries/fetchSummary',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      
      // Add filters if provided
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });
      
      const queryString = params.toString();
      const url = queryString ? `/api/queries/summary/?${queryString}` : '/api/queries/summary/';
      
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 
        error.message || 
        'Failed to fetch queries summary'
      );
    }
  }
);

/**
 * GET /api/queries/my-queries/
 * Get queries assigned to current user
 */
export const fetchMyClientQueriesThunk = createAsyncThunk(
  'queries/fetchMyQueries',
  async ({ offset = 0, limit = 20, ...filters } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ limit, offset });
      
      // Add additional filters if provided
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });
      
      const response = await axiosInstance.get(`/api/queries/my-queries/?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 
        error.message || 
        'Failed to fetch my queries'
      );
    }
  }
);